// Notification Service - Push notifications for study reminders

class NotificationService {
  constructor() {
    this.permission = 'default';
    this.isSupported = 'Notification' in window;
    this.scheduledReminders = [];
  }

  // Check if notifications are supported
  checkSupport() {
    return this.isSupported;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported) {
      console.log('Notifications not supported');
      return 'unsupported';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permission = permission;
      return permission;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return 'denied';
    }
  }

  // Send a notification immediately
  sendNotification(title, options = {}) {
    if (!this.isSupported || Notification.permission !== 'granted') {
      console.log('Cannot send notification - permission not granted');
      return null;
    }

    const defaultOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      vibrate: [200, 100, 200],
      requireInteraction: false,
      ...options
    };

    try {
      const notification = new Notification(title, defaultOptions);
      
      notification.onclick = () => {
        window.focus();
        notification.close();
        if (options.onClick) options.onClick();
      };

      return notification;
    } catch (error) {
      console.error('Error sending notification:', error);
      return null;
    }
  }

  // Schedule a daily study reminder
  scheduleDailyReminder(hour = 19, minute = 0) {
    // Clear any existing reminders
    this.clearScheduledReminders();

    const scheduleNext = () => {
      const now = new Date();
      const scheduledTime = new Date();
      scheduledTime.setHours(hour, minute, 0, 0);

      // If the time has passed today, schedule for tomorrow
      if (scheduledTime <= now) {
        scheduledTime.setDate(scheduledTime.getDate() + 1);
      }

      const timeUntilReminder = scheduledTime.getTime() - now.getTime();

      const timerId = setTimeout(() => {
        this.sendStudyReminder();
        // Schedule the next reminder
        scheduleNext();
      }, timeUntilReminder);

      this.scheduledReminders.push(timerId);
    };

    scheduleNext();
    
    // Store preference in localStorage
    localStorage.setItem('notificationSettings', JSON.stringify({
      enabled: true,
      hour,
      minute
    }));
  }

  // Clear all scheduled reminders
  clearScheduledReminders() {
    this.scheduledReminders.forEach(timerId => clearTimeout(timerId));
    this.scheduledReminders = [];
  }

  // Send a study reminder notification
  sendStudyReminder() {
    const messages = [
      { title: '📚 Hora de estudar!', body: 'Não perca seu streak! Faça uma lição rápida hoje.' },
      { title: '🔥 Mantenha seu streak!', body: 'Uma lição de 5 minutos faz toda a diferença.' },
      { title: '💪 Você consegue!', body: 'Seu inglês está esperando. Vamos praticar?' },
      { title: '🎯 Meta diária', body: 'Complete sua meta de estudo e ganhe XP!' },
      { title: '🌟 Momento de brilhar!', body: 'Dedique alguns minutos ao seu inglês hoje.' },
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    
    this.sendNotification(randomMessage.title, {
      body: randomMessage.body,
      tag: 'study-reminder',
      onClick: () => {
        window.location.href = '/tracks';
      }
    });
  }

  // Send streak warning notification
  sendStreakWarning(currentStreak) {
    if (currentStreak > 0) {
      this.sendNotification('⚠️ Seu streak está em risco!', {
        body: `Você tem um streak de ${currentStreak} dias. Não deixe acabar!`,
        tag: 'streak-warning',
        requireInteraction: true,
        onClick: () => {
          window.location.href = '/tracks';
        }
      });
    }
  }

  // Send achievement notification
  sendAchievementNotification(achievementName) {
    this.sendNotification('🏆 Nova Conquista!', {
      body: `Parabéns! Você desbloqueou: ${achievementName}`,
      tag: 'achievement'
    });
  }

  // Send level up notification
  sendLevelUpNotification(newLevel) {
    this.sendNotification('⬆️ Level Up!', {
      body: `Incrível! Você chegou ao nível ${newLevel}!`,
      tag: 'level-up'
    });
  }

  // Load saved notification settings
  loadSettings() {
    const saved = localStorage.getItem('notificationSettings');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  // Initialize notification service
  async initialize() {
    if (!this.isSupported) return false;

    const settings = this.loadSettings();
    if (settings?.enabled && Notification.permission === 'granted') {
      this.scheduleDailyReminder(settings.hour, settings.minute);
      return true;
    }
    return false;
  }

  // Disable notifications
  disable() {
    this.clearScheduledReminders();
    localStorage.setItem('notificationSettings', JSON.stringify({
      enabled: false
    }));
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
